import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileJson, FileText, Lock, CheckCircle2, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { OmCard, OmHead, Pill, GOLD, MUTED, NAVY, GREEN, FIELD } from "@/components/om/OmBits";
import { downloadRca } from "@/lib/rca";

const TONE = { Approved: "good", "Under Review": "warn", Draft: "gold", Rejected: "bad" };

export default function OmRcaDetail() {
  const { rcaId } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [reviewers, setReviewers] = useState([]);
  const [busy, setBusy] = useState("");

  const load = () => api.get(`/om/rca/${rcaId}`).then(({ data }) => setR(data))
    .catch((e) => toast.error(apiError(e.response?.data?.detail)));

  useEffect(() => {
    load();
    api.get("/om/rca").then(({ data }) => setReviewers(data.reviewers || [])).catch(() => {});
    /* eslint-disable-next-line */
  }, [rcaId]);

  const locked = r?.status === "Approved";

  const save = async (patch, message) => {
    setBusy("save");
    try {
      const { data } = await api.put(`/om/rca/${rcaId}`, patch);
      setR(data);
      toast.success(message || "RCA updated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const grab = async (fmt) => {
    setBusy(fmt);
    try { const name = await downloadRca(rcaId, fmt); toast.success(`${name} downloaded`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  if (!r) return <p className="text-[13px]" style={{ color: MUTED }}>Loading RCA…</p>;

  const setField = (k, v) => setR({ ...r, [k]: v });
  const setNote = (k, v) => setR({ ...r, why_notes: { ...(r.why_notes || {}), [k]: v } });

  return (
    <div data-testid="om-rca-detail-page">
      <button onClick={() => navigate("/om/rca")} className="inline-flex items-center gap-1.5 text-[12.5px] mb-3" style={{ color: MUTED }} data-testid="om-rca-back">
        <ArrowLeft className="w-3.5 h-3.5" /> RCA Library
      </button>

      <OmHead title="Root Cause Analysis" subtitle={`${r.ticket_code} · ${r.asset_code} ${r.asset_name}`}>
        <Pill tone={r.template_matched ? "good" : "warn"} testid="om-rca-match-badge">
          {r.template_matched ? "Template Match" : "Manual Scaffold"}
        </Pill>
        <Pill tone={TONE[r.status] || "muted"} testid="om-rca-status-badge">{r.status}</Pill>
      </OmHead>

      <OmCard className="mb-3" testid="om-rca-header-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-white font-semibold text-[16px]">{r.fault_name}</h2>
            <p className="text-[12.5px] mt-1" style={{ color: MUTED }}>{r.fault_description}</p>
          </div>
          <dl className="text-[11.5px] space-y-0.5 text-right" style={{ color: MUTED }}>
            <div>Generated {new Date(r.generated_at).toLocaleString("en-IN")}</div>
            <div>By {r.generated_by}</div>
            <div>Severity {r.severity} · similarity {Math.round((r.similarity_score || 0) * 100)}%</div>
            <div><Link to="/om/corrective-maintenance" className="hover:underline" style={{ color: GOLD }}>Open CM board</Link></div>
          </dl>
        </div>
        {locked ? (
          <div className="flex items-center gap-2 mt-3 text-[12.5px]" style={{ color: GREEN }} data-testid="om-rca-locked-note">
            <Lock className="w-3.5 h-3.5" /> Approved on {new Date(r.reviewed_at).toLocaleString("en-IN")} by {r.reviewed_by} — this record is read-only.
          </div>
        ) : null}
      </OmCard>

      <div className="space-y-2.5 mb-3" data-testid="om-rca-why-chain">
        {[1, 2, 3, 4, 5].map((n) => (
          <div key={n} className="border p-3.5" style={{ background: "#112240", borderColor: "rgba(201,168,76,0.16)", borderRadius: 8, borderLeft: `3px solid ${GOLD}` }} data-testid={`om-rca-why-${n}`}>
            <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold" style={{ color: GOLD }}>Why {n}</div>
            {locked ? (
              <p className="text-white text-[13.5px] mt-1.5 leading-relaxed">{r[`why_${n}`] || "—"}</p>
            ) : (
              <Textarea rows={2} value={r[`why_${n}`] || ""} onChange={(e) => setField(`why_${n}`, e.target.value)}
                onBlur={() => save({ [`why_${n}`]: r[`why_${n}`] || "" }, "Why statement saved")}
                placeholder={`Causal statement for Why ${n}`} className="mt-1.5 text-white" style={FIELD} data-testid={`om-rca-why-input-${n}`} />
            )}
            <Label className="text-[10.5px] uppercase tracking-[0.1em] mt-2.5 block" style={{ color: MUTED }}>Engineer annotation</Label>
            {locked ? (
              <p className="text-[12.5px] italic mt-1" style={{ color: MUTED }}>{(r.why_notes || {})[`why_${n}`] || "—"}</p>
            ) : (
              <Textarea rows={2} value={(r.why_notes || {})[`why_${n}`] || ""} onChange={(e) => setNote(`why_${n}`, e.target.value)}
                onBlur={() => save({ why_notes: r.why_notes || {} }, "Annotation saved")}
                placeholder="Site observation, evidence or correction" className="mt-1" style={FIELD} data-testid={`om-rca-note-input-${n}`} />
            )}
          </div>
        ))}
      </div>

      <div className="border p-4 mb-3" style={{ background: "#112240", borderColor: GOLD, borderRadius: 8 }} data-testid="om-rca-action-card">
        <h3 className="font-semibold text-[14px]" style={{ color: GOLD }}>Recommended corrective and preventive actions</h3>
        {locked ? (
          <p className="text-white text-[13px] mt-2 leading-relaxed">{r.recommended_action || "—"}</p>
        ) : (
          <Textarea rows={4} value={r.recommended_action || ""} onChange={(e) => setField("recommended_action", e.target.value)}
            onBlur={() => save({ recommended_action: r.recommended_action || "" }, "Recommended action saved")}
            className="mt-2" style={FIELD} data-testid="om-rca-action-input" />
        )}
        <Label className="text-[10.5px] uppercase tracking-[0.1em] mt-3 block" style={{ color: MUTED }}>Reviewing engineer notes</Label>
        {locked ? (
          <p className="text-[12.5px] italic mt-1" style={{ color: MUTED }}>{r.engineer_notes || "—"}</p>
        ) : (
          <Textarea rows={3} value={r.engineer_notes || ""} onChange={(e) => setField("engineer_notes", e.target.value)}
            onBlur={() => save({ engineer_notes: r.engineer_notes || "" }, "Notes saved")} className="mt-1" style={FIELD} data-testid="om-rca-engineer-notes" />
        )}
      </div>

      <OmCard className="mb-3" testid="om-rca-signoff-card">
        <h3 className="text-white font-semibold text-[14px] mb-3">Engineer sign-off</h3>
        {locked ? (
          <div className="flex items-center gap-2 text-[13px]" style={{ color: GREEN }} data-testid="om-rca-approved-badge">
            <CheckCircle2 className="w-4 h-4" /> Approved — reviewed by {r.reviewed_by} on {new Date(r.reviewed_at).toLocaleString("en-IN")}
          </div>
        ) : (
          <div className="grid sm:grid-cols-3 gap-3 items-end">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.1em]" style={{ color: MUTED }}>Reviewed by</Label>
              <select value={r.reviewed_by || ""} onChange={(e) => setField("reviewed_by", e.target.value)}
                className="w-full h-9 px-2 text-[13px] border" style={{ ...FIELD, borderRadius: 6 }} data-testid="om-rca-reviewer-select">
                <option value="">Select a manager</option>
                {reviewers.map((e) => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.1em]" style={{ color: MUTED }}>Review date</Label>
              <Input type="date" value={(r.review_date || "").slice(0, 10)} onChange={(e) => setField("review_date", e.target.value)} style={FIELD} data-testid="om-rca-review-date" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.1em]" style={{ color: MUTED }}>Status</Label>
              <select value={r.status} onChange={(e) => setField("status", e.target.value)}
                className="w-full h-9 px-2 text-[13px] border" style={{ ...FIELD, borderRadius: 6 }} data-testid="om-rca-status-select">
                {["Draft", "Under Review", "Approved", "Rejected"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="sm:col-span-3">
              <Button disabled={busy === "save"} onClick={() => save({
                reviewed_by: r.reviewed_by || "", review_date: r.review_date || "", status: r.status,
                why_notes: r.why_notes || {}, engineer_notes: r.engineer_notes || "",
                recommended_action: r.recommended_action || "",
                ...Object.fromEntries([1, 2, 3, 4, 5].map((n) => [`why_${n}`, r[`why_${n}`] || ""])),
              }, r.status === "Approved" ? "RCA approved and locked" : "Review submitted")}
                className="font-semibold" style={{ background: GOLD, color: NAVY }} data-testid="om-rca-submit-review">
                {busy === "save" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Submit review
              </Button>
            </div>
          </div>
        )}
      </OmCard>

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" disabled={!!busy} onClick={() => grab("json")} style={{ borderColor: `${GOLD}66`, color: GOLD, background: "transparent" }} data-testid="om-rca-download-json">
          {busy === "json" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileJson className="w-3.5 h-3.5 mr-1.5" />} Download JSON
        </Button>
        <Button disabled={!!busy} onClick={() => grab("pdf")} className="font-semibold" style={{ background: GOLD, color: NAVY }} data-testid="om-rca-download-pdf">
          {busy === "pdf" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1.5" />} Download PDF
        </Button>
        <span className="text-[11.5px] self-center" style={{ color: MUTED }}>
          {locked ? "The PDF carries the approval stamp." : "Draft PDFs carry a diagonal DRAFT watermark."}
        </span>
      </div>
    </div>
  );
}
